// Screens for a signed-in member the server will not serve yet (M10): their role requires 2-step
// verification they have not set up (the API answers only enrolment until they do), or they are
// connecting from a network their role's allow-list does not include.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Input, toast } from '@tatra/ui';
import QRCode from 'qrcode';
import { useState } from 'react';
import { errorMessage, post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { AuthLayout } from './AuthLayout.js';

export function MfaEnrolmentRequiredScreen() {
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);

  const start = useMutation({
    mutationFn: async () => {
      const s = await post<{ secret: string; otpauthUrl: string }>('/auth/me/mfa/setup');
      return { secret: s.secret, qr: await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 200 }) };
    },
    onSuccess: setSetup,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const enable = useMutation({
    mutationFn: () => post<{ recoveryCodes: string[] }>('/auth/me/mfa/enable', { code: code.trim() }),
    onSuccess: (r) => setCodes(r.recoveryCodes),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <AuthLayout
      title="Set up 2-step verification"
      subtitle="Your role requires a code from an authenticator app at sign-in. Set it up to continue."
    >
      {codes ? (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            Save these recovery codes. Each works once if you lose your phone; they won’t be shown again.
          </p>
          <ul className="grid grid-cols-2 gap-2 rounded-md border border-border bg-bg-elevated p-3 font-mono text-sm">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button className="w-full" onClick={() => void qc.invalidateQueries({ queryKey: ['me'] })}>
            Continue
          </Button>
        </div>
      ) : setup ? (
        <div className="space-y-3">
          <img
            src={setup.qr}
            alt="QR code for your authenticator app"
            className="mx-auto h-44 w-44 rounded-md bg-white p-2"
          />
          <p className="text-center text-xs break-all text-text-faint">
            Key: <span className="font-mono">{setup.secret}</span>
          </p>
          <Field label="6-digit code">
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
          <Button className="w-full" loading={enable.isPending} onClick={() => enable.mutate()}>
            Turn on and continue
          </Button>
        </div>
      ) : (
        <Button className="w-full" loading={start.isPending} onClick={() => start.mutate()}>
          Set up now
        </Button>
      )}
      <Button variant="ghost" className="mt-3 w-full" onClick={() => void signOut()}>
        Sign out
      </Button>
    </AuthLayout>
  );
}

export function NetworkBlockedScreen() {
  const { signOut } = useAuth();
  return (
    <AuthLayout
      title="Not available on this network"
      subtitle="Your role can only be used from approved networks, such as the office. Connect from one of them, or ask an administrator."
    >
      <Button className="w-full" onClick={() => void signOut()}>
        Sign out
      </Button>
    </AuthLayout>
  );
}
